import React from 'react';

interface UsageBannerProps {
  current: number;
  limit: number;
  remaining: number;
  resetsOn: string;
  featureName: string;
}

export const UsageBanner: React.FC<UsageBannerProps> = ({
  current,
  limit,
  remaining,
  resetsOn,
  featureName
}) => {
  const percentage = (current / limit) * 100;
  const resetDate = new Date(resetsOn).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  // Limit reached - show red warning
  if (remaining === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl">⛔</span>
          <div className="flex-1">
            <h4 className="font-semibold text-red-900 mb-1">Monthly Limit Reached</h4>
            <p className="text-sm text-red-700">
              You've used all <strong>{limit} {featureName}</strong> this month. 
              Your limit will reset on <strong>{resetDate}</strong>.
            </p>
            <p className="text-sm text-red-600 mt-2">
              Need more? Contact us about upgrading your plan.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Close to limit (80%+) - show yellow warning
  if (percentage >= 80) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl">⚠️</span>
          <div className="flex-1">
            <h4 className="font-semibold text-yellow-900 mb-1">Running Low</h4>
            <p className="text-sm text-yellow-700 mb-2">
              <strong>{remaining}</strong> of <strong>{limit} {featureName}</strong> remaining this month.
              Resets on <strong>{resetDate}</strong>.
            </p>
            <div className="w-full bg-yellow-200 rounded-full h-2">
              <div
                className="bg-yellow-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Normal status - show subtle blue info
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
      <div className="flex items-center justify-between text-sm">
        <span className="text-blue-800">
          <strong>{remaining}</strong> of <strong>{limit}</strong> {featureName} remaining
        </span>
        <span className="text-xs text-blue-600">Resets {resetDate}</span>
      </div>
      <div className="w-full bg-blue-200 rounded-full h-1.5 mt-2">
        <div
          className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};
